import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";


import {
    startClosure,
    finishClosure,
    setDetails,
    updateResult,
    setObservations,
    setLoading
} from "../../store/slices/cash/cashClosureSlice";

import useAlerts from "../useAlerts";
import useApiErrorHandler from "../useApiErrorHandler";
import useLoading from "../useLoading";

import CashClosureService from "../../services/cash/CashClosureService";
import { useCashReset } from "./useCashReset";
import { usePrintService } from "../printing/usePrintService";

export const useCashClosure = () => {

    const dispatch: AppDispatch = useDispatch();

    const {
        closureStarted,
        closureId,
        closureNumber,
        expectedCash,
        totalSales,
        countedCash,
        difference,
        details,
        observations,
        loading
    } = useSelector((state: RootState) => state.cashClosure);

    const { show: showAlert } = useAlerts();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const { resetCashFlow } = useCashReset();

    const { requestLegacyPrint } = usePrintService();

    /**
     * ▶️ Iniciar cierre
     */
    const start = async () => {

        try {

            showLoadingIndicator();
            dispatch(setLoading(true));

            const response = await CashClosureService.start();

            console.log(response);

            if (response?.statusCode === 200) {

                const data = response.data;

                dispatch(startClosure({
                    id: data.id,
                    closureNumber: data.closureNumber,
                    expectedCash: data.expectedCash,
                    totalSales: data.totalSales
                }));

                showAlert(response.type, response.title, response.message);

            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
            dispatch(setLoading(false));
        }

    };

    /**
     * 🔎 Cierre activo
     */
    const getActive = async () => {

        try {

            const response = await CashClosureService.getActive();

            console.log(response);


            if (response?.statusCode === 200 && response.data) {

                const data = response.data;

                dispatch(startClosure({
                    id: data.id,
                    closureNumber: data.closureNumber,
                    expectedCash: data.expectedCash,
                    totalSales: data.totalSales
                }));

            }

        } catch (error) {
            handle(error);
        }

    };

    /**
 * ✅ Finalizar cierre
 */
    const complete = async () => {

        if (!closureId) return;

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            const payload = {
                details,
                countedCash,
                observations
            };

            const response =
                await CashClosureService.complete(
                    closureId,
                    payload
                );

            console.log(response);

            if (response?.statusCode === 200) {

                const generatedClosureId =
                    response.data;

                showAlert(
                    response.type,
                    response.title,
                    response.message
                );

                dispatch(
                    finishClosure()
                );

                resetCashFlow();

                /**
                 * 🖨️ Imprimir automáticamente Corte Z
                 */
                void requestLegacyPrint('CASH_Z', generatedClosureId).catch(() => undefined);
            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(
                setLoading(false)
            );
        }
    };

    /**
     * 🧾 Denominaciones
     */
    const updateDenominations = (newDetails: any[]) => {

        dispatch(setDetails(newDetails));

        const counted = newDetails.reduce(
            (total, item) => total + (item.denomination * item.quantity),
            0
        );

        const diff = counted - expectedCash;

        dispatch(updateResult({
            countedCash: counted,
            difference: diff
        }));

    };

    /**
     * ✏️ Observaciones
     */
    const updateObservation = (value: string) => {
        dispatch(setObservations(value));
    };

    return {

        closureStarted,
        closureId,
        closureNumber,

        expectedCash,
        totalSales,

        countedCash,
        difference,

        details,
        observations,

        loading,

        start,
        getActive,
        complete,

        updateDenominations,
        updateObservation

    };

};
